import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import "@/index.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SubscriptionProvider, useSubscription } from "@/context/SubscriptionContext";
import { OrgSettingsProvider } from "@/context/OrgSettingsContext";
import { TenantBrandProvider } from "@/context/TenantBrandContext";
import { loadPlatformBranding, installFaviconLinks, usePlatformBranding, brandUrl } from "@/context/PlatformBrandingContext";
import GoogleAuthProvider from "@/context/GoogleAuthProvider";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Layout from "@/components/Layout";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import RequireEntitlement from "@/components/RequireEntitlement";
import { hasWorkspaceAccess, hasHrPortalAccess } from "@/components/layout/navVisibility";
import { isDualAccess, hasCertificationAccess, hasHrAccess, getPreferredWorkspace } from "@/lib/workspaceAccess";

// Public/auth-flow pages stay eager (small, hit immediately).
import Pricing from "@/pages/Pricing";
// Owner Dashboard is the default home route — keep eager so the first
// authenticated paint after login is instant.
import Dashboard from "@/pages/Dashboard";
import { PlatformAuthProvider } from "@/context/PlatformAuthContext";

// PRICING-MATRIX-ONLY (2026-02-27) — `/subscription/select-plan` no
// longer renders the legacy USD plan picker. It resolves to the single
// premium UAE Pricing Matrix experience. The route name is preserved
// only so existing in-app banners / bookmarks keep working; the legacy
// SelectPlan.jsx component has been deleted.
const SelectPlan            = lazy(() => import("@/pages/subscription/ChangePlan"));
const SubscriptionSuccess   = lazy(() => import("@/pages/subscription/Success"));
const SubscriptionCancelled = lazy(() => import("@/pages/subscription/Cancelled"));
const BillingStatus         = lazy(() => import("@/pages/subscription/BillingStatus"));
// Phase MODULE-PRICING — workspace selector when tenant has both modules.
const WorkspaceSelector     = lazy(() => import("@/pages/WorkspaceSelector"));

// Tenant ERP pages — code-split so non-Owner roles never pay the bytes
// of pages they cannot reach.
const MyWork        = lazy(() => import("@/pages/MyWork"));
const Leads         = lazy(() => import("@/pages/Leads"));
const LeadDetail    = lazy(() => import("@/pages/LeadDetail"));
const Quotations    = lazy(() => import("@/pages/Quotations"));
const Customers     = lazy(() => import("@/pages/Customers"));
const Vendors       = lazy(() => import("@/pages/Vendors"));
const Authorities   = lazy(() => import("@/pages/Authorities"));
const Products      = lazy(() => import("@/pages/Products"));
const Cases         = lazy(() => import("@/pages/Cases"));
// Phase HR-1A — HR module (tenant-scoped employee master + self-service).
const HrEmployees      = lazy(() => import("@/pages/hr/Employees"));
const HrEmployeeDetail = lazy(() => import("@/pages/hr/EmployeeDetail"));
const MyHrShell        = lazy(() => import("@/pages/hr/self/MyHR"));
const MyHrProfile      = lazy(() => import("@/pages/hr/self/MyProfile"));
const MyHrDocuments    = lazy(() => import("@/pages/hr/self/MyDocuments"));
const MyHrPayslips     = lazy(() => import("@/pages/hr/self/MyPayslips"));
const MyHrHome         = lazy(() => import("@/pages/hr/self/MyHrHome"));
const MyHrRequests     = lazy(() => import("@/pages/hr/self/MyRequests"));
const MyHrLeave        = lazy(() => import("@/pages/hr/self/MyLeave"));
const MyHrAttendance   = lazy(() => import("@/pages/hr/self/MyAttendance"));
const HrLeaveAdmin     = lazy(() => import("@/pages/hr/HrLeaveAdmin"));
const HrAttendance     = lazy(() => import("@/pages/hr/HrAttendance"));
const HrReimbursements = lazy(() => import("@/pages/hr/HrReimbursements"));
const MyHrReimbursements = lazy(() => import("@/pages/hr/self/MyReimbursements"));
const MyHrApprovals = lazy(() => import("@/pages/hr/self/MyHrApprovals"));
const HrTaxDocuments = lazy(() => import("@/pages/hr/HrTaxDocuments"));
const HrPolicies = lazy(() => import("@/pages/hr/HrPolicies"));
const HrVisits = lazy(() => import("@/pages/hr/HrVisits"));
const MyHrTaxDocuments = lazy(() => import("@/pages/hr/self/MyTaxDocuments"));
const MyPolicies = lazy(() => import("@/pages/hr/self/MyPolicies"));
const MyVisits = lazy(() => import("@/pages/hr/self/MyVisits"));
const HrDashboard      = lazy(() => import("@/pages/hr/HRDashboard"));
const HrSalaryStructures = lazy(() => import("@/pages/hr/SalaryStructures"));
const HrSettings       = lazy(() => import("@/pages/hr/HRSettings"));
const HrSalaryReadiness = lazy(() => import("@/pages/hr/SalaryReadiness"));
const HrPayrollRuns      = lazy(() => import("@/pages/hr/PayrollRuns"));
const HrPayrollRunDetail = lazy(() => import("@/pages/hr/PayrollRunDetail"));
const HrPayslipTemplateStudio = lazy(() => import("@/pages/hr/PayslipTemplateStudio"));
const HrMasters        = lazy(() => import("@/pages/hr/HrMasters"));
const HrProfileChangeRequests = lazy(() => import("@/pages/hr/HrProfileChangeRequests"));
const HrReports       = lazy(() => import("@/pages/hr/HrReports"));
const CaseDetail    = lazy(() => import("@/pages/CaseDetail"));
const Masters       = lazy(() => import("@/pages/Masters"));
const AdminSettings = lazy(() => import("@/pages/AdminSettings"));
const InvoiceTemplatesAdmin = lazy(() => import("@/pages/InvoiceTemplatesAdmin"));
const Proformas = lazy(() => import("@/pages/Proformas"));
const ProposalTemplatesAdmin = lazy(() => import("@/pages/ProposalTemplatesAdmin"));
const ProposalTemplateEditor = lazy(() => import("@/pages/ProposalTemplateEditor"));
const Documents     = lazy(() => import("@/pages/Documents"));
const Submissions   = lazy(() => import("@/pages/Submissions"));
const Surveillance  = lazy(() => import("@/pages/Surveillance"));
const Finance       = lazy(() => import("@/pages/Finance"));
const Renewals      = lazy(() => import("@/pages/Renewals"));
const Reports       = lazy(() => import("@/pages/Reports"));
// CertOpz Analytics Pack (2026-02) — advanced reports workspace and
// executive scorecard, gated by the same `module.reports` entitlement
// as the legacy /reports page. Backend router `routers/analytics.py`
// enforces the same feature gate + per-report permission checks.
const AnalyticsWorkspace  = lazy(() => import("@/pages/analytics/AnalyticsWorkspace"));
const ExecutiveScorecard  = lazy(() => import("@/pages/analytics/ExecutiveScorecard"));
const ClientPortal  = lazy(() => import("@/pages/ClientPortal"));
const RoleDashboard = lazy(() => import("@/pages/RoleDashboard"));

// Platform Console — totally separate audience (Super Admin only); never
// needed by tenant users so it's the highest-value split off the main
// bundle.
const PlatformLogin          = lazy(() => import("@/pages/platform/PlatformLogin"));
const PlatformLayout         = lazy(() => import("@/components/platform/PlatformLayout"));
const PlatformOverview       = lazy(() => import("@/pages/platform/PlatformOverview"));
const PlatformTenants        = lazy(() => import("@/pages/platform/PlatformTenants"));
const PlatformInvoiceTemplates = lazy(() => import("@/pages/platform/PlatformInvoiceTemplates"));
const PlatformBilling        = lazy(() => import("@/pages/platform/PlatformBilling"));
const PlatformSecurity       = lazy(() => import("@/pages/platform/PlatformSecurity"));
const PlatformHealth         = lazy(() => import("@/pages/platform/PlatformHealth"));
const PlatformPlans          = lazy(() => import("@/pages/platform/PlatformPlans"));
const PlatformPlanStudio     = lazy(() => import("@/pages/platform/PlatformPlanStudio"));
const PlatformUsageWatchdog  = lazy(() => import("@/pages/platform/PlatformUsageWatchdog"));
const PlatformPricing        = lazy(() => import("@/pages/platform/PlatformPricing"));
const PrivacyPolicy          = lazy(() => import("@/pages/legal/PrivacyPolicy"));
const TermsOfUse             = lazy(() => import("@/pages/legal/TermsOfUse"));
const PlatformFeatureFlags   = lazy(() => import("@/pages/platform/PlatformFeatureFlags"));
const PlatformServiceMatrix  = lazy(() => import("@/pages/platform/PlatformServiceMatrix"));
const PlatformIntegrations   = lazy(() => import("@/pages/platform/PlatformIntegrations"));
const PlatformAuditLog       = lazy(() => import("@/pages/platform/PlatformAuditLog"));
const PlatformAdminTeam      = lazy(() => import("@/pages/platform/PlatformAdminTeam"));
const PlatformSystemSettings = lazy(() => import("@/pages/platform/PlatformSystemSettings"));
const PlatformBranding       = lazy(() => import("@/pages/platform/PlatformBranding"));
const PlatformTenantProfitability = lazy(() => import("@/pages/platform/PlatformTenantProfitability"));

// Home route: layered dual-access-aware routing.
//   • Single-domain users → existing behaviour (RoleDashboard route or
//     the Owner Dashboard) — unaffected.
//   • Dual-access users with NO stored preference → /workspace picker.
//   • Dual-access users who last picked HR → /hr/dashboard.
//   • Dual-access users who last picked Certification → falls through
//     to the existing Dashboard / RoleDashboard logic so nothing else
//     changes for them.
// Layering is intentional: backend `default_dashboard_route` and
// `scope.py` remain the source of truth; this only interposes an
// additional preference layer for users who span BOTH modules.
function HomeRouter() {
  const { user } = useAuth();
  // HR-only plan (no Certification module): the certification dashboard
  // does not exist for this tenant — land on the HR dashboard instead.
  if (user && !hasCertificationAccess(user) && hasHrAccess(user)) {
    return <Navigate to="/hr/dashboard" replace />;
  }
  if (user && isDualAccess(user)) {
    const pref = getPreferredWorkspace();
    if (!pref) return <Navigate to="/workspace" replace />;
    if (pref === "hr") return <Navigate to="/hr/dashboard" replace />;
    // pref === "certification" → fall through to normal home logic.
  }
  if (user && user.default_dashboard_route && user.default_dashboard_route !== "/" && user.default_dashboard_route !== "/dashboard/owner") {
    return <Navigate to={user.default_dashboard_route} replace />;
  }
  return <Dashboard />;
}

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <BrandedLoader />;
  if (user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  return <Layout><SubscriptionGate>{children}</SubscriptionGate></Layout>;
}

/**
 * Phase USERMENU-RBAC (2026-07-09) — page-level guards for the two
 * sensitive dropdown items so a direct URL from an unauthorized role
 * (e.g. Consultant typing `/admin`) can never render the page. Non-
 * Owner/Admin visits redirect to the workspace home; canonical policy
 * matches the sidebar dropdown gating in `Layout.jsx` AND every backend
 * `/api/admin*` guard (`require_perm('admin.*')`).
 */
function AdminOnly({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <BrandedLoader />;
  if (user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  const roles = user?.roles || [];
  if (!roles.includes("Owner") && !roles.includes("Admin")) {
    return <Navigate to="/" replace />;
  }
  return <Layout><SubscriptionGate>{children}</SubscriptionGate></Layout>;
}

/**
 * `BillingOnly` — mirrors `SubscriptionContext.canBilling` (Owner ||
 * Admin || subscription.manage_payment_method || subscription.manage).
 * Uses a soft fallback while entitlements load (shows the branded
 * loader) so we never redirect mid-hydration.
 */
function BillingOnly({ children }) {
  const { user } = useAuth();
  const sub = useSubscription();
  const loc = useLocation();
  if (user === null) return <BrandedLoader />;
  if (user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (sub.loading) return <BrandedLoader />;
  if (!sub.canBilling) return <Navigate to="/" replace />;
  return children;
}

// Auth-only wrapper used by /subscription/* pages that should NOT run through
// the gate (otherwise we get redirect loops on EXPIRED/NONE statuses).
function AuthOnly({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <BrandedLoader />;
  if (user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function BrandedLoader() {
  // Platform-owned splash — consumes the centralized Website Logo
  // (Branding & Assets) with the bundled /logo.svg as final fallback.
  const branding = usePlatformBranding();
  const websiteLogo = brandUrl(branding?.website) || "/logo.svg";
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(180deg,#0F172A 0%,#1E293B 100%)" }} data-testid="auth-loading">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-white/95 flex items-center justify-center mb-4 animate-pulse">
          <img
            src={websiteLogo}
            alt="CertOpz"
            className="object-contain"
            style={{ width: "auto", height: "auto", maxWidth: 40, maxHeight: 40 }}
            onError={(e) => { if (e.currentTarget.src !== "/logo.svg") e.currentTarget.src = "/logo.svg"; }}
          />
        </div>
        <div className="text-white font-bold text-lg" style={{ fontFamily: "Plus Jakarta Sans" }}>CertOpz</div>
        <div className="text-cyan-300/70 text-[10px] uppercase tracking-widest mt-1">Loading workspace…</div>
      </div>
    </div>
  );
}

function RouteFallback() {
  // Lightweight fallback while a lazy chunk loads. Stays inside the
  // existing layout so the sidebar/topbar don't flash white.
  return (
    <div className="flex items-center justify-center py-24" data-testid="route-loading">
      <div className="text-xs text-slate-400 uppercase tracking-widest">Loading…</div>
    </div>
  );
}

// HR-LOGIN — separate-URL-same-auth model (2026-02-26).
// `<HrPortalGuard>` enforces self-service permission + a linked employee
// record (backend resolves both via /auth/me). `<MyHrLayout>` keeps the
// portal pages clear of the operational workspace sidebar.
function HrPortalGuard({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <BrandedLoader />;
  if (user === false) return <Navigate to="/hr-login" state={{ from: loc }} replace />;
  const perms = user?.permissions || [];
  const hasSelfService = perms.includes("hr.self_service");
  const isClient = (user?.roles || []).includes("Client User");
  if (isClient) {
    return <Navigate to="/login" replace />;
  }
  if (!hasSelfService) {
    return <Navigate to="/workspace" replace />;
  }
  return <SubscriptionGate>{children}</SubscriptionGate>;
}

/**
 * Frontend tech-debt sprint Part 1 — HR-only workspace guard.
 *
 * When a user carries ONLY HR access (self-service / portal permissions
 * but no workspace role like Owner/Admin/Sales/Operations/Finance/…),
 * the workspace home routes (`/`, `/dashboard`, `/workspace`, `/my-work`)
 * redirect to `/my-hr`. This keeps HR-only employees out of pages they
 * have no permissions for AND surfaces the tile-grid landing they
 * actually need.
 *
 * Order: this MUST live INSIDE `<Protected>` so the auth check runs
 * first (otherwise `user` may still be `null` or `false`).
 */
function HrOnlyRedirect({ children }) {
  const { user } = useAuth();
  if (user && typeof user === "object" && !hasWorkspaceAccess(user) && hasHrPortalAccess(user)) {
    return <Navigate to="/my-hr" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public */}
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/terms-of-use" element={<Navigate to="/terms" replace />} />
        <Route path="/login" element={<Login mode="workspace" />} />
        {/* HR-LOGIN — separate URL, same /api/auth/login endpoint.
            After auth, Login.jsx routes here-coming users to /my-hr. */}
        <Route path="/hr-login" element={<Login mode="hr-portal" />} />
        <Route path="/register" element={<Register />} />

        {/* Subscription flow (auth required, but NOT subscription-gated) */}
        {/* PRICING-MATRIX-ONLY — every legacy "Change Plan / Upgrade" CTA
            resolves here. Both /change-plan and /select-plan render
            ChangePlan; legacy SelectPlan.jsx was removed. */}
        <Route path="/subscription/select-plan" element={<AuthOnly><SelectPlan /></AuthOnly>} />
        <Route path="/subscription/change-plan" element={<AuthOnly><SelectPlan /></AuthOnly>} />
        <Route path="/workspace" element={<AuthOnly><WorkspaceSelector /></AuthOnly>} />
        <Route path="/subscription/success" element={<AuthOnly><SubscriptionSuccess /></AuthOnly>} />
        <Route path="/subscription/cancelled" element={<AuthOnly><SubscriptionCancelled /></AuthOnly>} />
        <Route path="/subscription/billing-status" element={<BillingOnly><BillingStatus /></BillingOnly>} />
        <Route path="/subscription/manage" element={<BillingOnly><BillingStatus /></BillingOnly>} />

        <Route path="/" element={<Protected><HrOnlyRedirect><HomeRouter /></HrOnlyRedirect></Protected>} />
        <Route path="/dashboard" element={<Protected><HrOnlyRedirect><HomeRouter /></HrOnlyRedirect></Protected>} />
        {/* NOTE: `/workspace` is declared ONCE above under AuthOnly →
            WorkspaceSelector. The earlier duplicate Protected route
            was removed 2026-07-20 — it shadowed the picker and forced
            dual-access users through the HomeRouter which is what we
            explicitly want the picker to short-circuit. */}
        <Route path="/dashboard/owner" element={<Protected><Dashboard /></Protected>} />
        <Route path="/dashboard/:roleKey" element={<Protected><RoleDashboard /></Protected>} />
        <Route path="/client/dashboard" element={<Protected><ClientPortal /></Protected>} />
        <Route path="/my-work" element={<Protected><HrOnlyRedirect><MyWork /></HrOnlyRedirect></Protected>} />
        <Route path="/leads" element={<Protected><RequireEntitlement module="certification" feature="module.leads"><Leads /></RequireEntitlement></Protected>} />
        <Route path="/leads/:id" element={<Protected><RequireEntitlement module="certification" feature="module.leads"><LeadDetail /></RequireEntitlement></Protected>} />
        <Route path="/quotations" element={<Protected><RequireEntitlement module="certification" feature="module.quotations"><Quotations /></RequireEntitlement></Protected>} />
        <Route path="/customers" element={<Protected><RequireEntitlement module="certification" feature="module.customers"><Customers /></RequireEntitlement></Protected>} />
        <Route path="/vendors" element={<Protected><Vendors /></Protected>} />
        <Route path="/authorities" element={<Protected><Authorities /></Protected>} />
        <Route path="/products" element={<Protected><RequireEntitlement module="certification" feature="module.products"><Products /></RequireEntitlement></Protected>} />
        <Route path="/cases" element={<Protected><RequireEntitlement module="certification" feature="module.cases"><Cases /></RequireEntitlement></Protected>} />
        <Route path="/cases/:id" element={<Protected><RequireEntitlement module="certification" feature="module.cases"><CaseDetail /></RequireEntitlement></Protected>} />
        <Route path="/documents" element={<Protected><RequireEntitlement module="certification" feature="module.documents"><Documents /></RequireEntitlement></Protected>} />
        <Route path="/submissions" element={<Protected><RequireEntitlement module="certification" feature="module.submissions"><Submissions /></RequireEntitlement></Protected>} />
        <Route path="/surveillance" element={<Protected><RequireEntitlement module="certification" feature="module.cases"><Surveillance /></RequireEntitlement></Protected>} />
        <Route path="/finance" element={<Protected><RequireEntitlement feature="module.finance"><Finance /></RequireEntitlement></Protected>} />
        <Route path="/finance/proformas" element={<Protected><RequireEntitlement feature="module.finance"><Proformas /></RequireEntitlement></Protected>} />
        <Route path="/finance/proformas/:id" element={<Protected><RequireEntitlement feature="module.finance"><Proformas /></RequireEntitlement></Protected>} />
        <Route path="/renewals" element={<Protected><RequireEntitlement module="certification" feature="module.renewals"><Renewals /></RequireEntitlement></Protected>} />
        <Route path="/reports" element={<Protected><RequireEntitlement feature="module.reports"><Reports /></RequireEntitlement></Protected>} />
        {/* Analytics Pack — advanced workspace + scorecard. Same
            `module.reports` gate as /reports; per-report row-level
            scope + permission is enforced by the backend. */}
        <Route path="/analytics" element={<Protected><RequireEntitlement feature="module.reports"><AnalyticsWorkspace /></RequireEntitlement></Protected>} />
        <Route path="/analytics/scorecard" element={<Protected><RequireEntitlement feature="module.reports"><ExecutiveScorecard /></RequireEntitlement></Protected>} />
        <Route path="/portal" element={<Protected><RequireEntitlement feature="collab.client_portal"><ClientPortal /></RequireEntitlement></Protected>} />
        <Route path="/masters" element={<Protected><Masters /></Protected>} />
        <Route path="/admin" element={<AdminOnly><AdminSettings /></AdminOnly>} />
        {/* Phase HR-1A — HR module routes. Backend `hr_permissions`
            helper is the security boundary; frontend pages use the
            `canHR()` helper for nav/page visibility. */}
        <Route path="/hr/employees" element={<Protected><RequireEntitlement module="hr" feature="module.hr.employees"><HrEmployees /></RequireEntitlement></Protected>} />
        <Route path="/hr/employees/:employeeId" element={<Protected><RequireEntitlement module="hr" feature="module.hr.employees"><HrEmployeeDetail /></RequireEntitlement></Protected>} />
        <Route path="/hr/dashboard" element={<Protected><RequireEntitlement module="hr" feature="module.hr.dashboard"><HrDashboard /></RequireEntitlement></Protected>} />
        <Route path="/hr/salary-readiness" element={<Protected><RequireEntitlement module="hr" feature="module.hr.payroll"><HrSalaryReadiness /></RequireEntitlement></Protected>} />
        <Route path="/hr/salary-structures" element={<Protected><RequireEntitlement module="hr" feature="module.hr.payroll"><HrSalaryStructures /></RequireEntitlement></Protected>} />
        <Route path="/hr/payroll-runs" element={<Protected><RequireEntitlement module="hr" feature="module.hr.payroll"><HrPayrollRuns /></RequireEntitlement></Protected>} />
        <Route path="/hr/payroll-runs/:runId" element={<Protected><RequireEntitlement module="hr" feature="module.hr.payroll"><HrPayrollRunDetail /></RequireEntitlement></Protected>} />
        <Route path="/hr/payslip-template" element={<Protected><RequireEntitlement module="hr" feature="module.hr.payslips"><HrPayslipTemplateStudio /></RequireEntitlement></Protected>} />
        <Route path="/hr/masters" element={<Protected><RequireEntitlement module="hr" feature="module.hr.settings"><HrMasters /></RequireEntitlement></Protected>} />
        <Route path="/hr/profile-change-requests" element={<Protected><RequireEntitlement module="hr" feature="module.hr.employees"><HrProfileChangeRequests /></RequireEntitlement></Protected>} />
        <Route path="/hr/reports" element={<Protected><RequireEntitlement module="hr" feature="module.hr.reports"><HrReports /></RequireEntitlement></Protected>} />
        <Route path="/hr/leave" element={<Protected><RequireEntitlement module="hr" feature="module.hr.leave"><HrLeaveAdmin /></RequireEntitlement></Protected>} />
        <Route path="/hr/attendance" element={<Protected><RequireEntitlement module="hr" feature="module.hr.attendance"><HrAttendance /></RequireEntitlement></Protected>} />
        <Route path="/hr/reimbursements" element={<Protected><RequireEntitlement module="hr" feature="module.hr.reimbursements"><HrReimbursements /></RequireEntitlement></Protected>} />
        <Route path="/hr/tax-documents" element={<Protected><RequireEntitlement module="hr" feature="module.hr.employees"><HrTaxDocuments /></RequireEntitlement></Protected>} />
        <Route path="/hr/policies" element={<Protected><RequireEntitlement module="hr" feature="module.hr.settings"><HrPolicies /></RequireEntitlement></Protected>} />
        <Route path="/hr/visits" element={<Protected><RequireEntitlement module="hr"><HrVisits /></RequireEntitlement></Protected>} />
        <Route path="/hr/settings" element={<Protected><RequireEntitlement module="hr" feature="module.hr.settings"><HrSettings /></RequireEntitlement></Protected>} />
        <Route path="/my-hr" element={<HrPortalGuard><MyHrShell /></HrPortalGuard>}>
          <Route index element={<MyHrHome />} />
          <Route path="home" element={<MyHrHome />} />
          <Route path="profile" element={<MyHrProfile />} />
          <Route path="documents" element={<MyHrDocuments />} />
          <Route path="payslips" element={<MyHrPayslips />} />
          <Route path="requests" element={<MyHrRequests />} />
          <Route path="leave" element={<MyHrLeave />} />
          <Route path="attendance" element={<MyHrAttendance />} />
          <Route path="reimbursements" element={<MyHrReimbursements />} />
          <Route path="approvals" element={<MyHrApprovals />} />
          <Route path="tax-documents" element={<MyHrTaxDocuments />} />
          <Route path="policies" element={<MyPolicies />} />
          <Route path="visits" element={<MyVisits />} />
        </Route>
        <Route path="/admin/invoice-templates" element={<AdminOnly><InvoiceTemplatesAdmin /></AdminOnly>} />
        <Route path="/admin/proposal-templates" element={<AdminOnly><ProposalTemplatesAdmin /></AdminOnly>} />
        <Route path="/admin/proposal-template" element={<AdminOnly><ProposalTemplateEditor /></AdminOnly>} />
        {/* Platform Console (separate auth) */}
        <Route path="/platform/login" element={<PlatformLogin />} />
        <Route path="/platform" element={<PlatformLayout />}>
          <Route index element={<Navigate to="/platform/overview" replace />} />
          <Route path="overview" element={<PlatformOverview />} />
          <Route path="tenants" element={<PlatformTenants />} />
          <Route path="invoice-templates" element={<PlatformInvoiceTemplates />} />
          <Route path="billing" element={<PlatformBilling />} />
          <Route path="tenant-profitability" element={<PlatformTenantProfitability />} />
          <Route path="security" element={<PlatformSecurity />} />
          <Route path="health" element={<PlatformHealth />} />
          {/* Plan Studio (Session 2 · 2026-07-10): legacy routes now
              redirect to the canonical editor. `/plans` and `/pricing`
              still exist as read-only screens for rollback but every
              nav path lands users on Plan Studio. */}
          <Route path="plans" element={<Navigate to="/platform/plan-studio" replace />} />
          <Route path="plans/legacy" element={<PlatformPlans />} />
          <Route path="plan-studio" element={<PlatformPlanStudio />} />
          <Route path="pricing" element={<Navigate to="/platform/plan-studio?tab=pricing" replace />} />
          <Route path="pricing/legacy" element={<PlatformPricing />} />
          <Route path="usage-watchdog" element={<PlatformUsageWatchdog />} />
          <Route path="feature-flags" element={<PlatformFeatureFlags />} />
          <Route path="service-matrix" element={<PlatformServiceMatrix />} />
          <Route path="integrations" element={<PlatformIntegrations />} />
          <Route path="audit-log" element={<PlatformAuditLog />} />
          <Route path="admin-team" element={<PlatformAdminTeam />} />
          <Route path="system-settings" element={<PlatformSystemSettings />} />
          <Route path="branding" element={<PlatformBranding />} />
        </Route>
        {/* Unknown URL → login page (signed-in users are bounced onward by Login itself). */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  // Install platform-branding favicon on boot. Silently no-ops if no
  // favicon has been uploaded. Cache-busted on every page load.
  useEffect(() => {
    loadPlatformBranding().then(assets => {
      installFaviconLinks(assets?.favicon || null);
    });
  }, []);

  return (
    <AuthProvider>
      <SubscriptionProvider>
        <PlatformAuthProvider>
          <OrgSettingsProvider>
            <TenantBrandProvider>
              <GoogleAuthProvider>
                <BrowserRouter>
                  <AppRoutes />
                  <Toaster position="top-right" richColors />
                </BrowserRouter>
              </GoogleAuthProvider>
            </TenantBrandProvider>
          </OrgSettingsProvider>
        </PlatformAuthProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
